import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

/**
 * Directive utilisée pour donner la capacité à une colonne d'une table de se redimensionner.
 */
@Directive({
    selector: '[appColumnResize]',
    standalone: true,
})
export class ColumnResizeDirective implements OnInit, OnDestroy {
    @Input() resizableTable: HTMLElement | null = null;

    /**
     * Position horizontale de départ de la colonne.
     */
    private startX!: number;

    /**
     * Largeur initiale de la colonne.
     */
    private startWidth!: number;

    /**
     * Valeur indiquant si la colonne est en cours de redimensionnement.
     */
    private isResizing = false;

    /**
     * Instance de l'élement HTML sur lequel est appliquée la directive (Cellule TH).
     */
    private htmlColumn: HTMLElement;

    /**
     *  Instance de l'element HTML utilisé pour redimensionner la colonne.
     */
    private resizer!: HTMLElement;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    private destroy$ = new Subject<void>();

    /**
     * Largeur de la colonne.
     */
    private width: number = 0;

    /**
     * Valeur indiquant si les largeurs des colonnes sont exprimées en pixels (true) ou en pourcentage (false).
     */
    private modePixels = true;

    /**
     * Valeyr représentant le nombre de pixels correspondant à une largeur de 1% (modePixels = false).
     */
    private pixelRatio: number = 1;

    /**
     * Emis lorsque la colonne est redimensionnée.
     */
    @Output() resizing = new EventEmitter<number>();

    /**
     * Emis lorsque le redimensionnement de la colonne est terminé.
     */
    @Output() resized = new EventEmitter();

    /**
     * Constructeur.
     * @param el : Reference de l'element sur lequel est appliquée la directive
     * @param renderer : Classe chargée du rendu du composant.
     * @param zone : Service permettant d'executer du travail en dehors de Zone.js.
     */
    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private zone: NgZone
    ) {
        this.htmlColumn = this.el.nativeElement;
    }

    /**
     * Appellée lorsque le composant est initialisé.
     */
    ngOnInit() {
        this.createResizer();
        this.initializeResizeListener();
    }

    /**
     * Appellée lorsque le composant est détruit.
     */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Crée l'element HTML utilisé pour redimensionner la colonne.
     */
    private createResizer() {
        this.resizer = this.renderer.createElement('div');
        this.renderer.addClass(this.resizer, 'column-resizer');
        this.renderer.setStyle(this.resizer, 'position', 'absolute');
        this.renderer.setStyle(this.resizer, 'right', '0');
        this.renderer.setStyle(this.resizer, 'top', '0');
        this.renderer.setStyle(this.resizer, 'width', '5px');
        this.renderer.setStyle(this.resizer, 'cursor', 'col-resize');
        this.renderer.appendChild(this.htmlColumn, this.resizer);
    }

    /**
     * Initialise les listeners de l'element HTML utilisé pour redimensionner la colonne.
     */
    private initializeResizeListener() {
        this.zone.runOutsideAngular(() => {
            // Début du drag&drop
            fromEvent(this.resizer, 'mousedown')
                .pipe(takeUntil(this.destroy$))
                .subscribe((event: MouseEvent) => this.onMouseDown(event));

            // Drag&drop
            fromEvent(document, 'mousemove')
                .pipe(debounceTime(1), takeUntil(this.destroy$))
                .subscribe((event: MouseEvent) => this.onMouseMove(event));

            // Fin du drag&drop
            fromEvent(document, 'mouseup')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.onMouseUp());
        });
    }

    /**
     * Appellée lorsque l'utilisateur clique avec le bouton gauche sur l'element HTML utilisé pour redimensionner la colonne.
     * @param event : Evenement de la souris.
     */
    private onMouseDown(event: MouseEvent) {
        event.preventDefault();
        // Indique le début du redimensionnement.
        this.isResizing = true;
        this.startX = event.pageX;
        // Stocke la largeur initiale de la colonne.
        this.startWidth = this.htmlColumn.offsetWidth;

        // Détermine si la largeur de la colonne est exprimé en %, Si c'est le cas détermine la valeur en pixels d'1%.
        const styleWidth = this.htmlColumn.style.getPropertyValue('width');
        if (styleWidth.endsWith('%')) {
            const percentWidth = Number.parseInt(styleWidth.replace('%', ''));
            this.modePixels = false;
            this.pixelRatio = this.startWidth / percentWidth;
        }
    }

    /**
     * Appellée lorsque l'utilisateur déplace la souris.
     * @param event : Evenement de la souris.
     */
    private onMouseMove(event: MouseEvent) {
        // L'evenement n'est traité que si cette colonne est en cours de redimensionnement.
        if (!this.isResizing) return;
        // Redimensionne l'élément HTML sur lequel est appliquée la directive (en général TH).
        this.width = this.startWidth + (event.pageX - this.startX);

        if (this.modePixels) {
            this.renderer.setStyle(this.htmlColumn, 'width', `${this.width}px`);
        } else {
            // Dans le cas de largeur de colonne en %, convertit le nombre de pixels dont s'est déplacé la souris en pourcentage.
            this.width = this.width / this.pixelRatio;
        }
        // Emet un evenement pour indiquer que la colonne à été redimensionnée.
        this.resizing.emit(this.width);
    }

    /**
     * Appellée lorsque l'utilisateur relache le bouton gauche de la souris alors qu'elle se trouve sur l'element HTML utilisé pour redimensionner la colonne.
     * @param event : Evenement de la souris.
     */
    private onMouseUp() {
        // L'evenement n'est traité que si cette colonne est en cours de redimensionnement.
        if (!this.isResizing) return;
        // Met fin au redimensionnement.
        this.isResizing = false;
        // Emet un evenement pour indiquer que le redimensionnement de la colonne est terminé.
        this.resized.emit();
    }
}
